import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from './api';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is logged in on app start
    const token = localStorage.getItem('admin_token');
    if (token) {
      setIsAuthenticated(true);
      // In a real app, you'd validate the token with the server
    }
    setLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const response = await apiService.login(email, password);
      if (response.success) {
        localStorage.setItem('admin_token', response.data.token);
        setIsAuthenticated(true);
        setUser(response.data.user);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Login failed:', error);
      
      // Fallback for demo credentials when backend is not available
      if (email === 'admin@gkr-tags.com' && password === 'admin123') {
        const mockUser: User = {
          id: 'admin-demo',
          email: 'admin@gkr-tags.com',
          first_name: 'Admin',
          last_name: 'User',
          role: 'admin'
        };
        
        localStorage.setItem('admin_token', 'demo-token-12345');
        setIsAuthenticated(true);
        setUser(mockUser);
        return true;
      }
      
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setIsAuthenticated(false);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}